import type { RequireAtLeastOne } from 'type-fest'
import type { TeadsEventBody, Uetq } from '@velasca/types'
import { $settings } from './globals'

interface EventBody {
  /**
   * The name of the event being tracked.
   */
  eventName: string
  /**
   * Additional information or context about the event being tracked. The specific format and content of the data parameter will depend on the event being tracked and the requirements of the analytics platform being used.
   */
  data: Record<string, unknown>
}

interface UetqEventBody {
  eventName: keyof Uetq['knownEvents']
  data: Partial<Uetq['knownParams']>
}

interface TeadsTrackEvent {
  eventName: TeadsEventBody['conversionType']
  data: Omit<TeadsEventBody, 'conversionType'>
}

interface TrackEventOptions {
  googleAnalytics: EventBody
  bingAds: UetqEventBody
  klaviyo: EventBody
  taboola: EventBody
  teads: TeadsTrackEvent
}

/**
 * This function tracks events using:
 * Google Analytics 4
 * Microsoft Bing ADS (UETQ Pixel)
 * Klaviyo
 * Taboola
 * Teads.
 *
 * GA4 parameters reference: https://developers.google.com/analytics/devguides/collection/ga4/reference/events.
 * BingAds (UETQ) parameters reference: https://help.ads.microsoft.com/apex/index/3/en/60123.
 * Klaviyo parameters reference: https://developers.klaviyo.com/en/docs/javascript_api#track-events-and-actions.
 * Teads parameters reference: https://help.teads.com/en/articles/6202162-collect-conversion-event-metadata.
 *
 * @param options - An object that accepts a minimum of one configuration object for an analytics platform.
 */
export const trackEvent = ({
  googleAnalytics,
  bingAds,
  klaviyo,
  taboola,
  teads,
}: RequireAtLeastOne<TrackEventOptions>): void => {
  if (googleAnalytics?.eventName && typeof window.gtag !== 'undefined') {
    const { eventName, data } = googleAnalytics

    gtag('event', eventName, data)
  }

  if (bingAds?.eventName && typeof window.uetq !== 'undefined') {
    const { eventName, data } = bingAds

    window.uetq.push('event', eventName, data)
  }

  if (
    klaviyo?.eventName &&
    typeof window.klaviyo !== 'undefined' &&
    $settings.klaviyo_enabled
  ) {
    const { eventName, data } = klaviyo

    window.klaviyo.push(['track', eventName, data])
  }

  if (taboola?.eventName && typeof window._tfa !== 'undefined') {
    const { eventName, data } = taboola

    window._tfa.push({
      notify: 'event',
      name: eventName,
      id: 1522741,
      ...data,
    })
  }

  if (teads?.eventName && typeof window.teads_e !== 'undefined') {
    const { eventName, data } = teads

    window.teads_e.push({
      conversionType: eventName,
      ...data,
    })
  }
}
